<template>
  <div v-if="showRoot" class="frf">
    <button
      v-if="showIdleReject"
      type="button"
      class="frf__reject-bar"
      @click="$emit('update:rejected', true)"
    >
      <span class="frf__radio" aria-hidden="true" />
      <span class="frf__reject-label">{{ $t('lbl_EMPBE_FieldRejection_Reject') }}</span>
    </button>

    <div v-else-if="showRejected" class="frf__panel" :class="{ 'frf__panel--customer': !isAdmin }">
      <template v-if="isAdmin">
        <div class="frf__header">
          <button
            type="button"
            class="frf__status frf__status--toggle"
            @click="$emit('update:rejected', false)"
          >
            <span class="frf__radio frf__radio--filled" aria-hidden="true" />
            <span class="frf__status-text">{{ $t('lbl_EMPBE_FieldRejection_StatusRejectedPrompt') }}</span>
            <span
              v-if="showRejectedCountBadge"
              class="frf__count-badge"
            >
              {{ $t('lbl_EMPBE_FieldRejection_RejectedCount', { count: rejectedCount }) }}
            </span>
          </button>
          <p v-if="actor" class="frf__actor">
            {{ $t('lbl_EMPBE_FieldRejection_ActionBy') }} {{ actor }}
          </p>
        </div>

        <div class="frf__composite" :class="{ 'frf__composite--error': !!displayError }">
          <div class="frf__field">
            <span v-if="!value" class="frf__placeholder" aria-hidden="true">
              {{ $t('lbl_EMPBE_FieldRejection_ReasonPlaceholder') }}
              <span class="frf__star">*</span>
            </span>
            <textarea
              ref="reasonInput"
              class="frf__input"
              :value="value"
              rows="1"
              aria-label="Rejection reason"
              :aria-invalid="!!displayError"
              @input="onReasonInput"
              @click.stop
              @mousedown.stop
            />
          </div>
          <b-dropdown
            v-if="hasTemplates"
            ref="templateDropdown"
            class="frf__template-dd"
            variant="outline-secondary"
            size="sm"
            right
            no-caret
            boundary="viewport"
            :popper-opts="templatePopperOpts"
            toggle-class="frf__template-btn"
            menu-class="frf__template-menu"
            @shown="bindTemplateScrollHide"
            @hidden="unbindTemplateScrollHide"
          >
            <template #button-content>
              <span>{{ $t('lbl_EMPBE_FieldRejection_TemplateLabel') }}</span>
              <span class="frf__chevron" aria-hidden="true" />
            </template>
            <b-dropdown-item
              v-for="t in templates"
              :key="t.id"
              @click="applyTemplate(t)"
            >
              {{ t.label }}
            </b-dropdown-item>
          </b-dropdown>
        </div>
        <p v-if="displayError" class="frf__error" role="alert">{{ displayError }}</p>
      </template>

      <template v-else>
        <span class="frf__customer-badge">{{ customerRejectedLabel }}</span>
        <p v-if="value" class="frf__customer-reason">{{ value }}</p>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FieldRejectionFeedback',
  props: {
    value: { type: String, default: '' },
    mode: { type: String, required: true },
    rejected: { type: Boolean, default: false },
    templates: { type: Array, default: () => [] },
    actor: { type: String, default: '' },
    /** 总驳回等场景：已勾选子项数量，>0 时显示红色统计角标 */
    rejectedCount: { type: Number, default: null },
    /** 外部传入的校验错误（如 Finish 时字段驳回原因为空） */
    error: { type: String, default: '' },
    /** 供应商侧驳回徽章文案；空则用默认 lbl_EMPBE_FieldRejection_StatusRejected */
    rejectedStatusLabel: { type: String, default: '' },
  },
  data() {
    return {
      errorMessage: '',
    };
  },
  computed: {
    isAdmin() {
      return this.mode === 'admin';
    },
    customerRejectedLabel() {
      const custom = String(this.rejectedStatusLabel || '').trim();
      if (custom) return custom;
      return this.$t('lbl_EMPBE_FieldRejection_StatusRejected');
    },
    templatePopperOpts() {
      return {
        positionFixed: true,
        modifiers: {
          preventOverflow: {
            boundariesElement: 'viewport',
            padding: 8,
          },
          flip: {
            behavior: ['bottom', 'top', 'left', 'right'],
          },
        },
      };
    },
    displayError() {
      return String(this.error || this.errorMessage || '').trim();
    },
    showIdleReject() {
      return this.isAdmin && !this.rejected;
    },
    showRejected() {
      if (!this.rejected) return false;
      // 管理员：勾选即展示可编辑驳回框
      if (this.isAdmin) return true;
      // 供应商：无驳回原因时不展示空壳
      if (this.mode === 'customer') {
        return !!String(this.value || '').trim();
      }
      return false;
    },
    showRoot() {
      return this.showIdleReject || this.showRejected;
    },
    hasTemplates() {
      return Array.isArray(this.templates) && this.templates.length > 0;
    },
    showRejectedCountBadge() {
      return typeof this.rejectedCount === 'number' && this.rejectedCount > 0;
    },
  },
  watch: {
    value() {
      this.syncReasonHeight();
    },
    rejected(isRejected) {
      if (!isRejected) {
        this.clearError();
      } else if (this.isAdmin) {
        this.$nextTick(() => this.syncReasonHeight());
      }
    },
  },
  mounted() {
    this.syncReasonHeight();
  },
  beforeDestroy() {
    this.unbindTemplateScrollHide();
  },
  methods: {
    onReasonInput(event) {
      this.clearError();
      this.$emit('clear-error');
      this.$emit('input', event.target.value);
      this.syncReasonHeight();
    },
    showError(message, options = {}) {
      const shouldScroll = options.scroll !== false;
      this.errorMessage = String(message || '').trim();
      this.$nextTick(() => {
        if (!shouldScroll) return;
        const el = this.$refs.reasonInput;
        if (el && typeof el.focus === 'function') el.focus();
        if (this.$el && typeof this.$el.scrollIntoView === 'function') {
          this.$el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        }
      });
    },
    clearError() {
      this.errorMessage = '';
    },
    syncReasonHeight() {
      const el = this.$refs.reasonInput;
      if (!el) return;
      el.style.height = 'auto';
      this.$nextTick(() => {
        const input = this.$refs.reasonInput;
        if (!input) return;
        input.style.height = `${input.scrollHeight}px`;
      });
    },
    applyTemplate(item) {
      this.clearError();
      this.$emit('clear-error');
      if (item && item.label != null) {
        this.$emit('input', item.label);
      }
    },
    bindTemplateScrollHide() {
      this.unbindTemplateScrollHide();
      this._hideTemplateOnScroll = (event) => {
        const menu = this.$el && this.$el.querySelector
          ? this.$el.querySelector('.frf__template-menu')
          : null;
        const target = event.target;
        if (menu && target && (target === menu || (typeof menu.contains === 'function' && menu.contains(target)))) {
          return;
        }
        const dd = this.$refs.templateDropdown;
        if (dd && typeof dd.hide === 'function') {
          dd.hide(false);
        }
      };
      window.addEventListener('scroll', this._hideTemplateOnScroll, true);
    },
    unbindTemplateScrollHide() {
      if (!this._hideTemplateOnScroll) return;
      window.removeEventListener('scroll', this._hideTemplateOnScroll, true);
      this._hideTemplateOnScroll = null;
    },
  },
};
</script>

<style scoped lang="scss">
.frf {
  width: 100%;
  box-sizing: border-box;
  pointer-events: auto !important;
}

.frf__reject-bar,
.frf__panel,
.frf__header,
.frf__status,
.frf__status--toggle,
.frf__composite,
.frf__field,
.frf__template-dd,
.frf__input {
  pointer-events: auto !important;
}

.frf__reject-bar {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  padding: 10px;
  border: 0;
  border-radius: 4px;
  background: rgba(115, 102, 255, 0.08);
  cursor: pointer;
  text-align: left;
}

.frf__radio {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid rgba(0, 0, 0, 0.4);
  box-sizing: border-box;
  position: relative;

  &--filled {
    border-color: #dc3545;

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #dc3545;
      transform: translate(-50%, -50%);
    }
  }
}

.frf__reject-label {
  font-size: 13px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.8);
}

.frf__panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  border-radius: 4px;
  background: #fcebed;
  box-sizing: border-box;
  overflow: visible;
}

.frf__panel--customer {
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
  padding: 8px;
}

.frf__customer-badge {
  flex-shrink: 0;
  color: #dc3545;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
}

.frf__customer-reason {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  color: rgba(0, 0, 0, 0.8);
  font-size: 14px;
  font-weight: 400;
  line-height: 1.3;
  word-break: break-word;
}

.frf__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
}

.frf__status {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.frf__status--toggle {
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
}

.frf__status-text {
  margin: 0;
  color: #dc3545;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  word-break: break-word;
}

.frf__count-badge {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #dc3545;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
}

.frf__actor {
  margin: 0;
  flex-shrink: 0;
  font-size: 12px;
  font-style: italic;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.4);
  white-space: nowrap;
}

.frf__composite {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-height: 42px;
  width: 100%;
  padding: 8px 8px 8px 12px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  background: #fff;
  box-sizing: border-box;
  overflow: visible;
}

.frf__composite--error {
  border-color: #dc3545;
}

.frf__field {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
}

.frf__placeholder {
  position: absolute;
  inset: 0;
  /* 必须穿透，否则盖住 textarea 导致无法聚焦输入 */
  pointer-events: none !important;
  font-size: 14px;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.frf__star {
  color: #dc3545;
}

.frf__input {
  display: block;
  width: 100%;
  min-height: 24px;
  margin: 0;
  padding: 0;
  border: 0;
  outline: none;
  resize: none;
  overflow: hidden;
  background: transparent;
  font-size: 14px;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.85);
  pointer-events: auto !important;
  caret-color: rgba(0, 0, 0, 0.85);
}

.frf__template-dd {
  flex-shrink: 0;
  align-self: flex-start;
  position: relative;
  z-index: 5;
}

.frf__template-dd ::v-deep .frf__template-btn,
.frf__template-dd ::v-deep .btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 4px 4px 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  background: #fff;
  color: rgba(0, 0, 0, 0.6);
  font-size: 12px;
  font-weight: 600;
  line-height: normal;
  box-shadow: none;
}
.frf__template-dd ::v-deep .frf__template-btn:hover,
.frf__template-dd ::v-deep .btn:hover {
 background-color: rgba(0, 0, 0, 0.03) !important;
 color: rgba(0, 0, 0, 0.60) !important;
 border-color: rgba(0, 0, 0, 0.10) !important;
}
.frf__template-dd ::v-deep .btn:focus {
 background-color: white !important;
 color: rgba(0, 0, 0, 0.60) !important;
 border-color: rgba(0, 0, 0, 0.10) !important;
}
.frf__chevron {
  width: 18px;
  height: 18px;
  display: inline-block;
  background: no-repeat center / 10px 6px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23666' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E");
}

.frf__error {
  margin: 0;
  color: #dc3545;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
  word-break: break-word;
}
</style>

<!-- dropdown-menu 常挂到 body 外，需非 scoped -->
<style lang="scss">
.dropdown-menu.frf__template-menu {
  z-index: 3000 !important;
  max-height: min(280px, calc(100vh - 140px));
  overflow-y: auto;
  overflow-x: hidden;
}
</style>
